// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-05-25 21:10:12 +0200 (Mon, 25 May 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 163262 $
//  Purpose     : log msg formatting
// =============================================================================
// ---Third-party imports
import now from 'performance-now';
// ---Framework imports
import { get, keys, size, isObject, isArray, isFloat, mapItems, isNumber, isNil, isString } from 'ldash';
// ---Globals
export const ACTIVE_ACTIONS = {
    'persist/REHYDRATE': true,
    'SERVER/EVENT': false
};
const unfiltered_log = process.env.UIX_ENV === 'express' // track all actions in ssr-tests
;
export let logFilter = (getState, action)=>unfiltered_log || !action.meta?.security && // messages containing stuff like credentials
    !action.meta?.silent && // probably messages that are to noisy
    // manual filtering
    !(ACTIVE_ACTIONS[action.type] !== undefined && !ACTIVE_ACTIONS[action.type]);
const formatStrOrObj = (e, levels = 2)=>{
    if (levels === 0) return '';
    if (isArray(e)) {
        return `Arr(${e.length})`;
    } else if (isFloat(e)) {
        return e.toFixed(4);
    } else if (isString(e) && e.length > 200) {
        return `${e.slice(0, 200)}[.. ${e.length} chars]`;
    } else if (!isObject(e)) {
        return e;
    } else {
        return mapItems(e, (k, v)=>`${k}:${formatStrOrObj(v, levels - 1)}`).join(' ');
    }
};
let ppStartEndVector = (action)=>(({ start: s, end: e, duration: d })=>`${s.x.toFixed(2)},${s.y.toFixed(2)} -> ${e.x.toFixed(2)},${e.y.toFixed(2)} [${d}s]`)(action.payload);
export let titleSuffix = (action)=>{
    let res = '';
    let { type, payload, meta = {} } = action;
    let details = ()=>{
        switch(type){
            case 'ROUTER_LOCATION_CHANGED':
                return payload.pathname + payload.search;
            case 'persist/REHYDRATE':
                return action.key;
            case 'ROUTER_LOCATION_CHANGED':
                return payload;
            case 'SET_APP_STATE':
                return payload.state;
            case 'SET_LANGUAGE':
                return payload.language;
            case 'SET_USER':
                return `${payload.user.name || 'guest'} ${payload.user.session.slice(0, 4)}`;
            case 'VIEWER_FRAME':
                return payload.i;
            case 'SET_IMAGE_CONTROL':
                return `${payload.src.slice(-15)} ${payload.state}`;
            case 'SMOOTH_SCROLL_DONE':
                return payload.animationId;
            case 'SMOOTH_SCROLL':
                return `${meta.animationId} ${ppStartEndVector(action)}`;
            case 'GET_TREE_CHILDREN':
                return `${payload.idContext}/${payload.otid}`;
            case 'SET_TREE_CHILDREN':
                return `${payload.idContext}/${payload.otid || 0}`;
            case 'GO':
                return `${meta.runId} - ${formatStrOrObj(payload.urlOrRequest)}`;
            case 'SET_FILTER_DATA':
                {
                    let partnames = keys(payload.filterData);
                    return partnames.length > 0 ? partnames.join(',') : 'EMPTY';
                }
            case 'SET_DOMAIN_DATA':
                return payload.name;
            case 'INIT_DOMAIN':
                return `${payload.dname} - ${payload.reason}`;
            case 'ACTIVATE_VIEW':
                return payload.runId;
            case 'SET_ACTIVE_TREE_NODE':
                return get(payload, 'node.id');
            case 'SET_DEPLOY_CONFIG':
                return `${payload.mode}/${payload.package} ${payload.apiurl || '(empty apiurl)'}`;
            case 'SET_WEBAUTHN':
                return payload.state;
            case 'SET_DEBUG_DATA':
                return keys(payload).join(', ');
            case 'HOT_RELOAD':
                return payload.status;
            case 'CALL_API':
                return `${payload.name} - ${formatStrOrObj(payload.params)}`;
            case 'REDUCE_API':
                return `${payload.name} - ${formatStrOrObj(payload.params)}`;
            case 'REDUCE_API_RESULT':
                return `${payload.name} - ${formatStrOrObj(get(payload, 'data', {}))}`;
            case 'GET_TREE_RECORDS':
                return `${payload.fields.length} x ${payload.nodes.length}`;
            case 'SET_TREE_LIST':
                return `${payload.data.length} rows from ${payload.listid}`;
            case 'ui/setValue':
                return `${meta.name} - ${formatStrOrObj(payload.value.__raw__ ?? payload.value)} ${!isNil(payload.version) ? '[v' + payload.version + ']' : ''}`;
            case 'UPLOAD_FILE':
                return `${payload.fileid} - init ${payload.bytes}`;
            case 'SET_UPLOAD_STATE':
                return `${payload.fileid} - ${payload.state}`;
            case 'SERVER/EVENT':
                return meta.eventName;
            case 'WEBSOCKET_DEBUG':
                return payload.operation;
            case 'SET_EDITED_WIKI_CODE':
                return payload.storeKey;
            case 'API_CALL_START':
                return payload.path;
            case 'API_CALL_END':
                return `${payload.path} [${payload.status}]`;
            case 'LOAD_CHUNKS':
                return String(payload.names);
            case 'SET_LOADED_CHUNKS':
                return String(payload.names);
            default:
                return '';
        }
    };
    res = details();
    if (res && res.length > 100) {
        res = res.slice(100);
    }
    if (res !== '') {
        res = ' | ' + res;
    }
    return res;
};
let fmtTime = ()=>(now() / 1000).toFixed(3);
export let titleFormatter = (action, time, took)=>`${fmtTime()} uix:action ${action.type} +${took.toFixed(0)}ms${titleSuffix(action)}`;
